import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  BaseSelection,
  FORMAT_TEXT_COMMAND,
  LexicalEditor
} from 'lexical';
import { $createHeadingNode, $createQuoteNode } from '@lexical/rich-text';
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND
} from '@lexical/list';
import { TOGGLE_LINK_COMMAND } from '@lexical/link';
import { $setBlocksType } from '@lexical/selection';

/**
 * Apply bold formatting
 */
export function formatBold(editor: LexicalEditor): void {
  editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
}

/**
 * Apply italic formatting
 */
export function formatItalic(editor: LexicalEditor): void {
  editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
}

/**
 * Apply strikethrough formatting
 */
export function formatStrike(editor: LexicalEditor): void {
  editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough');
}

/**
 * Apply underline formatting
 */
export function formatUnderline(editor: LexicalEditor): void {
  editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
}

/**
 * Format selection as a heading
 */
export function formatHeading(
  editor: LexicalEditor,
  tag: 'h1' | 'h2' | 'h3',
  currentBlockType: string
): void {
  editor.update(() => {
    const selection = $getSelection();
    if (!$isRangeSelection(selection)) return;

    // Toggle: if already this heading type, convert to paragraph
    if (currentBlockType === tag) {
      $setBlocksType(selection, () => $createParagraphNode());
    } else {
      $setBlocksType(selection, () => $createHeadingNode(tag));
    }
  });
}

/**
 * Format selection as a quote
 */
export function formatQuote(
  editor: LexicalEditor,
  currentBlockType: string
): void {
  editor.update(() => {
    const selection = $getSelection();
    if (!$isRangeSelection(selection)) return;

    if (currentBlockType === 'quote') {
      $setBlocksType(selection, () => $createParagraphNode());
    } else {
      $setBlocksType(selection, () => $createQuoteNode());
    }
  });
}

/**
 * Format selection as a paragraph
 */
export function formatParagraph(editor: LexicalEditor): void {
  editor.update(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      $setBlocksType(selection, () => $createParagraphNode());
    }
  });
}

/**
 * Convert selection to appropriate list type
 */
export function convertToAppropriateList(
  editor: LexicalEditor,
  selection: BaseSelection,
  listType: 'bullet' | 'number',
  currentBlockType: string
): void {
  // First convert to paragraph if it's another list type
  const otherListType = listType === 'bullet' ? 'number' : 'bullet';
  if (currentBlockType === otherListType) {
    $setBlocksType(selection, () => $createParagraphNode());
  }

  // Then apply the appropriate list command
  const listCommand =
    listType === 'bullet'
      ? INSERT_UNORDERED_LIST_COMMAND
      : INSERT_ORDERED_LIST_COMMAND;

  editor.dispatchCommand(listCommand, undefined);
}

/**
 * Format selection as a list
 */
export function formatList(
  editor: LexicalEditor,
  listType: 'bullet' | 'number',
  currentBlockType: string
): void {
  // Check if we need to toggle off
  if (currentBlockType === listType) {
    formatParagraph(editor);
    return;
  }

  // Apply the appropriate list command
  editor.update(() => {
    const selection = $getSelection();
    if (!$isRangeSelection(selection)) return;

    convertToAppropriateList(editor, selection, listType, currentBlockType);
  });
}

/**
 * Format as a link with the provided URL
 */
export function formatLink(editor: LexicalEditor, url: string | null): void {
  editor.dispatchCommand(TOGGLE_LINK_COMMAND, url);
}
